import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Calendar, Clock, Tag, User, Share2, 
  Twitter, Linkedin, Link as LinkIcon, ChevronRight,
  BookOpen
} from 'lucide-react';
import MarkdownRenderer from '../components/MarkdownRenderer';
import { getPostBySlug, getRelatedPosts, getAllPosts } from '../services/blogService';

// Table of Contents Component
function TableOfContents({ content }) {
  const [headings, setHeadings] = useState([]);
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    // Extract headings from markdown content
    const headingRegex = /^(#{2,3})\s+(.+)$/gm;
    const matches = [];
    let match;
    
    while ((match = headingRegex.exec(content)) !== null) {
      const level = match[1].length;
      const text = match[2];
      const id = text.toLowerCase().replace(/[^\w]+/g, '-');
      matches.push({ level, text, id });
    }
    
    setHeadings(matches);
  }, [content]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -66% 0px' }
    );

    headings.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [headings]);

  if (headings.length === 0) return null;

  return (
    <nav className="sticky top-24">
      <h4 className="flex items-center gap-2 mb-4 text-sm font-semibold text-gray-900">
        <BookOpen className="w-4 h-4" />
        Table des matières
      </h4>
      <ul className="space-y-2 text-sm">
        {headings.map(({ level, text, id }) => (
          <li key={id}>
            <a
              href={`#${id}`}
              className={`block py-1 transition-colors ${
                level === 3 ? 'pl-4' : ''
              } ${
                activeId === id
                  ? 'text-indigo-600 font-medium'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              {text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

// Share Button Component
function ShareButton({ title, url }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <Share2 className="w-4 h-4" />
        Partager
      </button>
      
      {showDropdown && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-50"
        >
          <a
            href={shareLinks.twitter}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <Twitter className="w-4 h-4" />
            Twitter
          </a>
          <a
            href={shareLinks.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <Linkedin className="w-4 h-4" />
            LinkedIn
          </a>
          <button
            onClick={copyLink}
            className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full"
          >
            <LinkIcon className="w-4 h-4" />
            {copied ? 'Copié!' : 'Copier le lien'}
          </button>
        </motion.div>
      )}
    </div>
  );
}

// Related Posts Component
function RelatedPosts({ posts }) {
  if (posts.length === 0) return null;

  return (
    <section className="mt-16 pt-12 border-t border-gray-200">
      <h3 className="text-2xl font-bold text-gray-900 mb-8">Articles similaires</h3>
      <div className="grid gap-6 md:grid-cols-3">
        {posts.map((post) => (
          <Link
            key={post.slug}
            to={`/blog/${post.slug}`}
            className="group p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
          >
            <span className="inline-block px-3 py-1 text-xs font-medium text-indigo-600 bg-indigo-100 rounded-full mb-3">
              {post.category}
            </span>
            <h4 className="font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors line-clamp-2">
              {post.title}
            </h4>
            <p className="mt-2 text-sm text-gray-500 line-clamp-2">
              {post.description}
            </p>
            <div className="flex items-center gap-2 mt-4 text-sm text-indigo-600 font-medium">
              Lire l'article
              <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

// Navigation between posts
function PostNavigation({ currentSlug }) {
  const allPosts = getAllPosts();
  const currentIndex = allPosts.findIndex(p => p.slug === currentSlug);
  const prevPost = currentIndex < allPosts.length - 1 ? allPosts[currentIndex + 1] : null;
  const nextPost = currentIndex > 0 ? allPosts[currentIndex - 1] : null;

  return (
    <div className="flex flex-col sm:flex-row gap-4 mt-12 pt-8 border-t border-gray-200">
      {prevPost ? (
        <Link
          to={`/blog/${prevPost.slug}`}
          className="flex-1 group p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
        >
          <span className="text-sm text-gray-500">← Article précédent</span>
          <p className="font-medium text-gray-900 group-hover:text-indigo-600 transition-colors line-clamp-1">
            {prevPost.title}
          </p>
        </Link>
      ) : <div className="flex-1" />}
      
      {nextPost ? (
        <Link
          to={`/blog/${nextPost.slug}`}
          className="flex-1 group p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors text-right"
        >
          <span className="text-sm text-gray-500">Article suivant →</span>
          <p className="font-medium text-gray-900 group-hover:text-indigo-600 transition-colors line-clamp-1">
            {nextPost.title}
          </p>
        </Link>
      ) : <div className="flex-1" />}
    </div>
  );
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadPost = async () => {
      setIsLoading(true);
      const postData = getPostBySlug(slug);
      
      if (!postData) {
        navigate('/articles', { replace: true });
        return;
      }
      
      setPost(postData);
      setRelatedPosts(getRelatedPosts(slug));
      setIsLoading(false);
    };

    loadPost();
  }, [slug, navigate]);

  if (isLoading) {
    return (
      <main className="min-h-screen pt-20 bg-white">
        <div className="max-w-4xl px-4 py-16 mx-auto">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-gray-200 rounded w-3/4" />
            <div className="h-4 bg-gray-200 rounded w-1/2" />
            <div className="space-y-4">
              <div className="h-4 bg-gray-200 rounded" />
              <div className="h-4 bg-gray-200 rounded" />
              <div className="h-4 bg-gray-200 rounded w-5/6" />
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (!post) return null;

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <main className="min-h-screen pt-20 bg-white">
      {/* Header */}
      <header className="relative py-16 overflow-hidden bg-gradient-to-br from-gray-900 via-indigo-900 to-gray-900">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:32px_32px]" />
        <div className="absolute top-20 right-10 w-72 h-72 rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="absolute bottom-10 left-10 w-96 h-96 rounded-full bg-purple-500/20 blur-3xl" />
        
        <div className="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <Link
              to="/articles"
              className="inline-flex items-center gap-2 mb-8 text-sm font-medium text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour aux articles
            </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-1.5 text-sm font-medium text-indigo-300 bg-indigo-500/20 rounded-full mb-6">
              {post.category}
            </span>
            
            <h1 className="text-4xl font-bold text-white md:text-5xl lg:text-6xl leading-tight">
              {post.title}
            </h1>
            
            <p className="mt-6 text-xl text-gray-300">
              {post.description}
            </p>

            <div className="flex flex-wrap items-center gap-6 mt-8 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{new Date(post.date).toLocaleDateString('fr-FR', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{post.readTime} de lecture</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-6">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-gray-300 bg-white/10 rounded-full"
                >
                  <Tag className="w-3 h-3" />
                  {tag}
                </span>
              ))}
            </div>
          </motion.div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8 py-12">
        <div className="flex gap-12">
          {/* Main Content */}
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex-1 max-w-4xl"
          >
            {/* Share button */}
            <div className="flex justify-end mb-8">
              <ShareButton title={post.title} url={currentUrl} />
            </div>

            {/* Article content */}
            <MarkdownRenderer content={post.content} />

            {/* Post navigation */}
            <PostNavigation currentSlug={slug} />

            {/* Related posts */}
            <RelatedPosts posts={relatedPosts} />
          </motion.article>

          {/* Sidebar - Table of Contents */}
          <aside className="hidden xl:block w-64 flex-shrink-0">
            <TableOfContents content={post.content} />
          </aside>
        </div>
      </div>
    </main>
  );
}
